import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { plansForSport } from '../../services/planTierOffers';
import { lookupSignup } from '../../services/signup';
import type { SignupLookupResponse, SignupSport } from '../../types/signup';
import { SignupHeader } from './SignupLayout';
import { SignupPlanChoice } from './SignupPlanChoice';
import { SignupPriceConfirmation } from './SignupPriceConfirmation';
import { formatPrice } from './signupFormat';

/**
 * Shown when the create call refused the chosen plan (400 `invalid_plan_tier`): an admin took it off
 * sale or changed it after the lookup. The account already exists and is verified, so only the plan
 * is asked again. The lookup is re-run for the current plans; if that fails, the plans already
 * known are offered and the server checks the choice again on Continue.
 */
export const SignupPlanStep = ({
  email,
  lookup,
  sport,
  message,
  onContinue,
  onStartOver,
}: {
  email: string;
  lookup: SignupLookupResponse;
  sport: SignupSport;
  /** The server's own words for the refusal. */
  message: string;
  onContinue: (lookup: SignupLookupResponse, planTier: string | undefined) => void;
  onStartOver: () => void;
}) => {
  const [current, setCurrent] = useState(lookup);
  const [refreshing, setRefreshing] = useState(true);
  const [selected, setSelected] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    lookupSignup(email)
      .then((fresh) => {
        // Anything but eligible says nothing new about plans; keep what we have.
        if (!cancelled && fresh.status === 'eligible') setCurrent(fresh);
      })
      .catch(() => {
        // Not fatal: the known plans stay on screen and Continue asks the server again.
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email]);

  const plans = plansForSport(current.planTiers, sport);
  // Nothing is picked for the coach, as on the account step: Continue waits for a choice. The plan
  // the server refused is still offered if the fresh lookup sells it again.
  const chosen = plans.find((plan) => plan.key === selected);

  return (
    <>
      <SignupHeader title="Choose your plan" subtitle={email} />

      <div
        className="accept-invite-message accept-invite-message--error"
        role="alert"
        data-testid="signup-plan-error"
      >
        <p>{message}</p>
        <p>Pick a plan below to keep going.</p>
      </div>

      {refreshing ? (
        <p className="signup-status" role="status" data-testid="signup-plan-loading">
          Checking the latest plans...
        </p>
      ) : (
        plans.length > 0 && (
          <SignupPlanChoice
            plans={plans}
            currency={current.currency}
            threeYearOfferEndsOn={current.threeYearOfferEndsOn ?? null}
            value={chosen?.key}
            onChange={setSelected}
          />
        )
      )}

      {!refreshing && plans.length === 0 && (
        <p className="signup-status" role="status" data-testid="signup-plan-none">
          No plans are on sale right now. Start over, or talk to us about your program.
        </p>
      )}

      {!refreshing && chosen && (
        <SignupPriceConfirmation
          price={formatPrice(chosen.annualPriceCents, current.currency)}
          trialDays={current.trialDays}
        />
      )}

      <div className="accept-invite-actions">
        <Button
          type="button"
          fullWidth
          disabled={refreshing || !chosen}
          onClick={() => onContinue(current, chosen?.key)}
          data-testid="signup-plan-continue"
        >
          Continue
        </Button>
        <button
          type="button"
          className="signup-text-button"
          onClick={onStartOver}
          data-testid="signup-start-over"
        >
          Start over with a different email
        </button>
      </div>
    </>
  );
};
